import { Helmet } from 'react-helmet-async';
import { Box, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import type { ReactNode } from 'react';

import ENV from "../../env";
import Logo from "../logo";

export type ErrorPageProps = {
    code: number;
    title: string;
    /** The browser tab title, e.g. "Page Not Found". */
    tabTitle: string;
    illustration: { src: string; alt: string };
    children: ReactNode;
};

/** The frame of an error page: the logo, a workshop illustration, the error code, what happened and where to go next. */
export function ErrorPage({ code, title, tabTitle, illustration, children }: ErrorPageProps) {
    return (
        <>
            <Helmet>
                <title>{ tabTitle } | { ENV.APP_NAME }</title>
            </Helmet>

            <Stack sx={ {
                minHeight: '100vh', boxSizing: 'border-box', px: { xs: 2, sm: 6 }, pt: 4, pb: 6, bgcolor: '#F4F6F8', color: '#161C24',
                fontFamily: (theme) => theme.typography.fontFamily,
            } }>
                <Box component="header"><Logo/></Box>

                <Stack component="main" direction={ { xs: 'column', md: 'row' } } alignItems="center" justifyContent="center"
                       spacing={ { xs: 4, md: 7 } } sx={ { flexGrow: 1, py: 4 } }>
                    <Box component="img" src={ illustration.src } alt={ illustration.alt } sx={ {
                        width: { xs: '100%', md: 560 }, maxWidth: 560, aspectRatio: '4 / 3', objectFit: 'cover', borderRadius: '24px', flexShrink: 0, display: 'block',
                    } }/>

                    <Stack spacing={ 2 } sx={ { width: '100%', maxWidth: 460 } }>
                        <Box component="span" sx={ {
                            alignSelf: 'flex-start', fontSize: 12, fontWeight: 800, letterSpacing: '0.06em', px: 1.25, py: 0.625, borderRadius: '8px',
                            bgcolor: '#E8F0FC', color: '#1A55B3',
                        } }>
                            ERROR { code }
                        </Box>
                        <Typography component="h1" variant="inherit" sx={ { fontSize: 36, lineHeight: '42px', fontWeight: 800, letterSpacing: '-0.02em' } }>
                            { title }
                        </Typography>
                        { children }
                    </Stack>
                </Stack>
            </Stack>
        </>
    );
}

export type ErrorPageActionProps = {
    to?: string;
    onClick?: () => void;
    primary?: boolean;
    children: ReactNode;
};

/** A way out of an error page: a link to another page, or a button doing something here. */
export function ErrorPageAction({ to, onClick, primary, children }: ErrorPageActionProps) {
    const sx = {
        display: 'inline-flex', alignItems: 'center', height: 48, px: 2.75, borderRadius: '12px', boxSizing: 'border-box',
        border: `1px solid ${ primary ? '#2065D1' : '#CFD6DE' }`, bgcolor: primary ? '#2065D1' : '#FFFFFF', color: primary ? '#FFFFFF' : '#161C24',
        font: 'inherit', fontSize: 15, fontWeight: 700, textDecoration: 'none', cursor: 'pointer',
        '&:hover': { bgcolor: primary ? '#103996' : '#F4F6F8' },
        '&:focus-visible': { outline: '2px solid #2065D1', outlineOffset: 2 },
    };

    return to
        ? <Box component={ RouterLink } to={ to } sx={ sx }>{ children }</Box>
        : <Box component="button" type="button" onClick={ onClick } sx={ sx }>{ children }</Box>;
}

export const ERROR_TEXT_SX = { fontSize: 16, lineHeight: '25px', color: '#4F5B67' } as const;
